// src/components/Footer.jsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCity } from '../context/CityContext';
import '../styles/Footer.css';
import logo from '../assets/caryugo1-logo.jpeg';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { setSelectedCity } = useCity();
  const navigate = useNavigate();

  const activeCities = ['Indore', 'Ujjain', 'Dewas', 'Pithampur', 'Mhow'];
  const upcomingCities = [
    'Delhi NCR','Bangalore','Hyderabad','Mumbai','Pune',
    'Delhi','Gurgaon','Noida','Ahmedabad','Chennai',
    'Kolkata','Lucknow','Jaipur','Agra','Ambala',
    'Chandigarh','Coimbatore','Faridabad','Ghaziabad',
    'Kanpur','Karnal','Kochi','Mysuru','Sonipat','Visakhapatnam'
  ];
  const cities = [...activeCities, ...upcomingCities];

  const handleCityClick = (city) => {
    setSelectedCity(city);
    const params = new URLSearchParams();
    params.set('location', city);
    if (!activeCities.includes(city)) params.set('comingSoon', 'true');
    navigate(`/buy-cars?${params.toString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="footer">
      <div className="footer-gold-bar" />

      {/* ─── Indore City Skyline — highly visible gold/white SVG watermark ─── */}
      <div className="footer-skyline" aria-hidden="true">
        <svg
          className="footer-skyline-svg"
          viewBox="0 0 1600 440"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMax meet"
        >
          <defs>
            {/* Fade: buildings visible at bottom, fade to transparent at top */}
            <linearGradient id="skyFade2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="white" stopOpacity="0" />
            <stop offset="40%"  stopColor="white" stopOpacity="0.35" />
            <stop offset="100%" stopColor="white" stopOpacity="0.9" />
            </linearGradient>

            {/* Gold glow filter for landmark buildings */}
            <filter id="goldGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>

            <filter id="softGlow" x="-10%" y="-10%" width="120%" height="120%">
              <feGaussianBlur stdDeviation="1.2" result="blur"/>
              <feMerge>
                <feMergeNode in="blur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>

            {/* Mask for fade effect over entire SVG */}
            <mask id="fadeMask">
              <rect x="0" y="0" width="1600" height="440" fill="url(#skyFade2)"/>
            </mask>
          </defs>

          {/* All buildings grouped and masked for fade effect */}
          <g mask="url(#fadeMask)">

            {/* Ground line */}
            <line x1="0" y1="390" x2="1600" y2="390"
              stroke="rgba(255,214,10,0.8)" strokeWidth="1.5"/>

            {/* ── FAR LEFT generic buildings ── */}
            <rect x="0"   y="285" width="36"  height="105" fill="none" stroke="rgba(255,214,10,0.8)" strokeWidth="1.2"/>
            {[300,315,330,345,360,375].map(y=><line key={y} x1="0" y1={y} x2="36" y2={y} stroke="rgba(255,214,10,0.2)" strokeWidth="0.6"/>)}
            <rect x="40"  y="255" width="28"  height="135" fill="none" stroke="rgba(255,214,10,0.8)" strokeWidth="1.2"/>
            {[270,285,300,315,330,345,360,375].map(y=><line key={y} x1="40" y1={y} x2="68" y2={y} stroke="rgba(255,214,10,0.2)" strokeWidth="0.6"/>)}
            <rect x="72"  y="295" width="30"  height="95"  fill="none" stroke="rgba(255,214,10,0.4)"  strokeWidth="1"/>
            <rect x="106" y="225" width="44"  height="165" fill="none" stroke="rgba(255,214,10,0.5)" strokeWidth="1.4"/>
            {[243,261,279,297,315,333,351,369].map(y=><line key={y} x1="106" y1={y} x2="150" y2={y} stroke="rgba(255,214,10,0.22)" strokeWidth="0.6"/>)}
            <rect x="114" y="218" width="28" height="8" fill="none" stroke="rgba(255,214,10,0.55)" strokeWidth="0.9"/>
            <line x1="128" y1="210" x2="128" y2="218" stroke="rgba(255,214,10,0.7)" strokeWidth="1.5"/>

            {/* ── CLOCK TOWER (Ghanta Ghar) x:158–206 — GOLD LANDMARK ── */}
            <g filter="url(#goldGlow)">
              <rect x="158" y="310" width="44"  height="80"  fill="none" stroke="#FFD60A" strokeWidth="1.6"/>
              <rect x="166" y="260" width="28"  height="52"  fill="none" stroke="#FFD60A" strokeWidth="1.4"/>
              <rect x="170" y="250" width="20"  height="12"  fill="none" stroke="#FFD60A" strokeWidth="1.2"/>
              {/* Clock face */}
              <circle cx="180" cy="278" r="11" fill="none" stroke="#FFD60A" strokeWidth="1.4"/>
              <line x1="180" y1="272" x2="180" y2="278" stroke="#FFD60A" strokeWidth="1.1"/>
              <line x1="180" y1="278" x2="187" y2="278" stroke="#FFD60A" strokeWidth="1.1"/>
              {/* Tower spire */}
              <line x1="180" y1="238" x2="180" y2="220" stroke="#FFD60A" strokeWidth="1.6"/>
              <polygon points="180,212 186,222 174,222" fill="rgba(255,214,10,0.3)" stroke="#FFD60A" strokeWidth="1.1"/>
              {/* Arched doorways */}
              {[164,182].map((x,i)=>(
                <path key={i} d={`M${x} 390 L${x} 338 Q${x+9} 326 ${x+18} 338 L${x+18} 390`}
                  fill="none" stroke="rgba(255,214,10,0.55)" strokeWidth="1"/>
              ))}
            </g>

            {/* ── LALBAGH PALACE (neoclassical) x:220–385 — WHITE/GOLD ── */}
            <g filter="url(#softGlow)">
              <rect x="220" y="286" width="165" height="104"  fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.4"/>
              {[232,250,268,286,304,322,340,358,376].map((x,i)=>(
                <g key={i}>
                  <line x1={x} y1="286" x2={x} y2="390" stroke="rgba(255,255,255,0.35)" strokeWidth="0.9"/>
                  <rect x={x-4} y="282" width="8" height="6" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.8"/>
                </g>
              ))}
              {/* Pediment */}
              <path d="M220 286 L302 228 L385 286" fill="none" stroke="#FFD60A" strokeWidth="1.8"/>
              <path d="M240 286 L302 238 L365 286" fill="none" stroke="rgba(255,214,10,0.4)" strokeWidth="0.9"/>
              {/* Dome */}
              <path d="M276 228 Q302 198 328 228" fill="none" stroke="#FFD60A" strokeWidth="1.6"/>
              <rect x="288" y="198" width="28" height="32" fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="1"/>
              <path d="M283 198 Q302 180 321 198" fill="none" stroke="#FFD60A" strokeWidth="1.6"/>
              <line x1="302" y1="180" x2="302" y2="164" stroke="#FFD60A" strokeWidth="1.6"/>
              <circle cx="302" cy="161" r="5" fill="rgba(255,214,10,0.4)" stroke="#FFD60A" strokeWidth="1.1"/>
              {/* Windows */}
              {[256,290,324].map((x,i)=>(
                <g key={i}>
                  <rect x={x} y="306" width="22" height="36" rx="2" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.9"/>
                  <path d={`M${x} 319 Q${x+11} 311 ${x+22} 319`} fill="none" stroke="rgba(255,214,10,0.5)" strokeWidth="0.8"/>
                  <rect x={x} y="350" width="22" height="30" rx="2" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.8"/>
                </g>
              ))}
            </g>

            {/* ── MID-LEFT generic ── */}
            <rect x="390" y="268" width="38" height="122" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.1"/>
            {[285,300,315,330,345,360,375].map(y=><line key={y} x1="390" y1={y} x2="428" y2={y} stroke="rgba(255,255,255,0.18)" strokeWidth="0.5"/>)}
            <rect x="432" y="290" width="30" height="100"  fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1"/>
            <rect x="466" y="238" width="52" height="152" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2"/>
            {[256,274,292,310,328,346,364,382].map(y=><line key={y} x1="466" y1={y} x2="518" y2={y} stroke="rgba(255,255,255,0.18)" strokeWidth="0.5"/>)}
            <rect x="474" y="230" width="36" height="9" fill="none" stroke="rgba(255,214,10,0.7)" strokeWidth="1"/>
            <line x1="492" y1="222" x2="492" y2="230" stroke="#FFD60A" strokeWidth="1.6"/>

            {/* ── RAJWADA PALACE (simplified stepped silhouette) x:522–778 — GOLD HERO ── */}
            <g filter="url(#goldGlow)">
              {/* Stone base floors 1–2 */}
              <rect x="522" y="305" width="256" height="85"  fill="none" stroke="#FFD60A" strokeWidth="2"/>
              <line x1="522" y1="348" x2="778" y2="348" stroke="rgba(255,214,10,0.5)" strokeWidth="1"/>
              {/* Ground floor arches */}
              {[530,556,582,608,634,660,686,712,738,764].map((x,i)=>(
                <path key={i} d={`M${x} 390 L${x} 357 Q${x+12} 345 ${x+24} 357 L${x+24} 390`}
                  fill="none" stroke="rgba(255,214,10,0.7)" strokeWidth="1.1"/>
              ))}
              {/* First floor arches */}
              {[530,556,582,608,634,660,686,712,738,764].map((x,i)=>(
                <path key={i} d={`M${x} 348 L${x} 315 Q${x+12} 304 ${x+24} 315 L${x+24} 348`}
                  fill="none" stroke="rgba(255,214,10,0.55)" strokeWidth="0.9"/>
              ))}
              {/* Grand gate arch */}
              <path d="M626 390 L626 310 Q650 288 674 310 L674 390"
                fill="none" stroke="#FFD60A" strokeWidth="2.5"/>
              <path d="M634 390 L634 320 Q650 305 666 320 L666 390"
                fill="none" stroke="rgba(255,214,10,0.5)" strokeWidth="1.1"/>
              {/* Floor 3 */}
              <rect x="542" y="256" width="216" height="51" fill="none" stroke="#FFD60A" strokeWidth="1.6"/>
              {[552,576,600,624,648,672,696,720].map((x,i)=>(
                <path key={i} d={`M${x} 305 L${x} 267 Q${x+11} 257 ${x+22} 267 L${x+22} 305`}
                  fill="none" stroke="rgba(255,214,10,0.65)" strokeWidth="1"/>
              ))}
              {/* Floor 4 */}
              <rect x="564" y="213" width="172" height="45" fill="none" stroke="#FFD60A" strokeWidth="1.4"/>
              {[574,596,618,640,662,684,706,724].map((x,i)=>(
                <path key={i} d={`M${x} 256 L${x} 224 Q${x+10} 215 ${x+20} 224 L${x+20} 256`}
                  fill="none" stroke="rgba(255,214,10,0.55)" strokeWidth="0.9"/>
              ))}
              {/* Floor 5 */}
              <rect x="586" y="176" width="128" height="39" fill="none" stroke="#FFD60A" strokeWidth="1.2"/>
              {[596,616,636,656,676,692].map((x,i)=>(
                <path key={i} d={`M${x} 213 L${x} 186 Q${x+9} 178 ${x+18} 186 L${x+18} 213`}
                  fill="none" stroke="rgba(255,214,10,0.5)" strokeWidth="0.85"/>
              ))}
              {/* Floor 6 */}
              <rect x="608" y="146" width="84"  height="32" fill="none" stroke="#FFD60A" strokeWidth="1.1"/>
              {[618,634,650,666,678].map((x,i)=>(
                <path key={i} d={`M${x} 176 L${x} 156 Q${x+8} 148 ${x+16} 156 L${x+16} 176`}
                  fill="none" stroke="rgba(255,214,10,0.8)"strokeWidth="0.8"/>
              ))}
              {/* Floor 7 */}
              <rect x="628" y="116" width="44"  height="32" fill="none" stroke="#FFD60A" strokeWidth="1.4"/>
              {/* Roof chattris */}
              {[634,644,650,662,668].map((cx,i)=>(
                <g key={i}>
                  <path d={`M${cx-8} 118 Q${cx} 104 ${cx+8} 118`} fill="rgba(255,214,10,0.15)" stroke="#FFD60A" strokeWidth="1.1"/>
                  <line x1={cx} y1="104"  x2={cx} y2="94" stroke="#FFD60A" strokeWidth="1.2"/>
                  <circle cx={cx} cy="92" r="3" fill="rgba(255,214,10,0.5)" stroke="#FFD60A" strokeWidth="0.9"/>
                </g>
              ))}
            </g>

            {/* ── MID RIGHT generic ── */}
            <rect x="782" y="256" width="42"  height="134" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.1"/>
            {[272,288,304,320,336,352,368,384].map(y=><line key={y} x1="782" y1={y} x2="824" y2={y} stroke="rgba(255,255,255,0.18)" strokeWidth="0.5"/>)}
            <rect x="828" y="285" width="32"  height="105"  fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1"/>
            <rect x="864" y="235" width="56"  height="155" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2"/>
            {[252,270,288,306,324,342,360,378].map(y=><line key={y} x1="864" y1={y} x2="920" y2={y} stroke="rgba(255,255,255,0.18)" strokeWidth="0.5"/>)}
            <rect x="872" y="227" width="40" height="9" fill="none" stroke="rgba(255,214,10,0.7)" strokeWidth="1"/>
            <line x1="892" y1="219" x2="892" y2="227" stroke="#FFD60A" strokeWidth="1.6"/>

            {/* ── TALL COMMERCIAL TOWER x:924–992 ── */}
            <rect x="924" y="194" width="68"  height="196" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="1.4"/>
            {[212,230,248,266,284,302,320,338,356,374].map(y=><line key={y} x1="924" y1={y} x2="992" y2={y} stroke="rgba(255,255,255,0.2)" strokeWidth="0.55"/>)}
            {[4,3,2,1,0].map(row=>[934,952,970].map((x,ci)=>(
              <rect key={`${row}-${ci}`} x={x} y={212+row*36} width="14" height="24"
                fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.4)" strokeWidth="0.7"/>
            )))}
            <rect x="932" y="187" width="52"  height="8" fill="none" stroke="rgba(255,214,10,0.8)" strokeWidth="1.1"/>
            <rect x="940" y="168" width="36"  height="20" fill="none" stroke="rgba(255,214,10,0.7)" strokeWidth="1.1"/>
            <line x1="958" y1="160" x2="958" y2="168" stroke="#FFD60A" strokeWidth="1.8"/>
            <rect x="996" y="248" width="50"  height="142" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="1"/>
            {[264,280,296,312,328,344,360,376].map(y=><line key={y} x1="996" y1={y} x2="1046" y2={y} stroke="rgba(255,255,255,0.16)" strokeWidth="0.5"/>)}

            {/* ── DOMED CIVIC BUILDING x:1052–1188 — WHITE/GOLD ── */}
            <g filter="url(#softGlow)">
              <rect x="1052" y="272" width="136" height="118" fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="1.4"/>
              {[1064,1080,1096,1112,1128,1144,1160,1176].map((x,i)=>(
                <line key={i} x1={x} y1="272" x2={x} y2="390" stroke="rgba(255,255,255,0.28)" strokeWidth="0.8"/>
              ))}
              {/* Main arch dome */}
              <path d="M1052 272 Q1120 205 1188 272" fill="none" stroke="#FFD60A" strokeWidth="2"/>
              <path d="M1072 272 Q1120 218 1168 272" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.9"/>
              {/* Central tower */}
              <rect x="1104" y="208" width="32"  height="66" fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="1.1"/>
              <path d="M1098 208 Q1120 188 1142 208" fill="none" stroke="#FFD60A" strokeWidth="1.6"/>
              <line x1="1120" y1="188" x2="1120" y2="173" stroke="#FFD60A" strokeWidth="1.6"/>
              <circle cx="1120" cy="170" r="5.5" fill="rgba(255,214,10,0.4)" stroke="#FFD60A" strokeWidth="1.1"/>
              {/* Windows */}
              {[1064,1088,1112,1136,1158].map((x,i)=>(
                <g key={i}>
                  <rect x={x} y="305" width="18" height="28" rx="2" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.8"/>
                  <path d={`M${x} 319 Q${x+9} 312 ${x+18} 319`} fill="none" stroke="rgba(255,214,10,0.55)" strokeWidth="0.8"/>
                  <rect x={x} y="340" width="18" height="28" rx="2" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.75"/>
                </g>
              ))}
            </g>

            {/* ── MID-FAR RIGHT generic ── */}
            <rect x="1192" y="255" width="46"  height="135" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.1"/>
            {[272,288,304,320,336,352,368,384].map(y=><line key={y} x1="1192" y1={y} x2="1238" y2={y} stroke="rgba(255,255,255,0.18)" strokeWidth="0.5"/>)}
            <rect x="1244" y="215" width="62"  height="175" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2"/>
            {[232,250,268,286,304,322,340,358,376].map(y=><line key={y} x1="1244" y1={y} x2="1306" y2={y} stroke="rgba(255,255,255,0.18)" strokeWidth="0.5"/>)}
            {[4,3,2,1].map(row=>[1250,1268,1286].map((x,ci)=>(
              <rect key={`r${row}-${ci}`} x={x} y={232+row*38} width="12" height="26"
                fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.38)" strokeWidth="0.65"/>
            )))}
            <rect x="1252" y="208" width="46"  height="8" fill="none" stroke="rgba(255,214,10,0.72)" strokeWidth="1"/>
            <line x1="1275" y1="200" x2="1275" y2="208" stroke="#FFD60A" strokeWidth="1.6"/>

            {/* ── MOSQUE / MINARET x:1312–1362 — GOLD LANDMARK ── */}
            <g filter="url(#goldGlow)">
              <rect x="1312" y="292" width="46"  height="98"  fill="none" stroke="#FFD60A" strokeWidth="1.6"/>
              {/* Minarets */}
              <rect x="1312" y="250" width="13"  height="140" fill="none" stroke="#FFD60A" strokeWidth="1.2"/>
              <rect x="1345" y="250" width="13"  height="140" fill="none" stroke="#FFD60A" strokeWidth="1.2"/>
              {/* Minaret tops */}
              <path d="M1312 250 Q1318.5 234 1325 250" fill="rgba(255,214,10,0.2)" stroke="#FFD60A" strokeWidth="1.2"/>
              <path d="M1345 250 Q1351.5 234 1358 250" fill="rgba(255,214,10,0.2)" stroke="#FFD60A" strokeWidth="1.2"/>
              <line x1="1318.5" y1="234" x2="1318.5" y2="222" stroke="#FFD60A" strokeWidth="1.4"/>
              <line x1="1351.5" y1="234" x2="1351.5" y2="222" stroke="#FFD60A" strokeWidth="1.4"/>
              <circle cx="1318.5" cy="220" r="3.5" fill="rgba(255,214,10,0.5)" stroke="#FFD60A" strokeWidth="1"/>
              <circle cx="1351.5" cy="220" r="3.5" fill="rgba(255,214,10,0.5)" stroke="#FFD60A" strokeWidth="1"/>
              {/* Central dome arch */}
              <path d="M1322 292 Q1335 272 1348 292" fill="rgba(255,214,10,0.12)" stroke="#FFD60A" strokeWidth="1.4"/>
              <rect x="1328" y="272" width="14"  height="22" fill="none" stroke="rgba(255,214,10,0.7)" strokeWidth="1"/>
              <line x1="1335" y1="265" x2="1335" y2="256" stroke="#FFD60A" strokeWidth="1.2"/>
              <circle cx="1335" cy="254" r="4" fill="rgba(255,214,10,0.4)" stroke="#FFD60A" strokeWidth="1"/>
              {/* Mosque façade arch */}
              <path d="M1316 390 L1316 322 Q1335 306 1354 322 L1354 390"
                fill="none" stroke="#FFD60A" strokeWidth="1.5"/>
            </g>

            {/* ── FAR RIGHT modern buildings ── */}
            <rect x="1368" y="228" width="56"  height="162" fill="none" stroke="rgba(255,255,255,0.52)" strokeWidth="1.1"/>
            {[246,264,282,300,318,336,354,372].map(y=><line key={y} x1="1368" y1={y} x2="1424" y2={y} stroke="rgba(255,255,255,0.18)" strokeWidth="0.5"/>)}
            {[4,3,2,1].map(row=>[1374,1390,1406].map((x,ci)=>(
              <rect key={`fr${row}-${ci}`} x={x} y={244+row*36} width="12" height="24"
                fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.36)" strokeWidth="0.6"/>
            )))}
            <rect x="1376" y="220" width="40"  height="9" fill="none" stroke="rgba(255,214,10,0.7)" strokeWidth="0.95"/>
            <line x1="1396" y1="212" x2="1396" y2="220" stroke="#FFD60A" strokeWidth="1.4"/>
            <rect x="1428" y="276" width="40"  height="114" fill="none" stroke="rgba(255,255,255,0.42)" strokeWidth="1"/>
            {[292,308,324,340,356,372].map(y=><line key={y} x1="1428" y1={y} x2="1468" y2={y} stroke="rgba(255,255,255,0.16)" strokeWidth="0.5"/>)}
            <rect x="1472" y="246" width="62"  height="144" fill="none" stroke="rgba(255,255,255,0.48)" strokeWidth="1.1"/>
            {[264,282,300,318,336,354,372].map(y=><line key={y} x1="1472" y1={y} x2="1534" y2={y} stroke="rgba(255,255,255,0.16)" strokeWidth="0.5"/>)}
            <rect x="1480" y="238" width="46"  height="9" fill="none" stroke="rgba(255,214,10,0.65)" strokeWidth="0.9"/>
            <line x1="1503" y1="230" x2="1503" y2="238" stroke="#FFD60A" strokeWidth="1.4"/>
            <rect x="1538" y="282" width="36"  height="108"  fill="none" stroke="rgba(255,255,255,0.38)" strokeWidth="0.9"/>
            <rect x="1576" y="300" width="24"  height="90"  fill="none" stroke="rgba(255,255,255,0.3)"  strokeWidth="0.75"/>

            {/* ── PALM TREES ── */}
            {[[152,390],[488,390],[806,390],[1048,390],[1198,390],[1408,390]].map(([bx,by],bi)=>(
              <g key={bi}>
                <path d={`M${bx} ${by} Q${bx+3} ${by-40} ${bx+6} ${by-80}`}
                  fill="none" stroke="rgba(255,214,10,0.7)" strokeWidth="2.2"/>
                {[[-26,6],[-14,-6],[-3,-13],[7,-15],[19,-9],[27,0]].map(([dx,dy],fi)=>(
                  <path key={fi}
                    d={`M${bx+6} ${by-80} Q${bx+6+dx*0.52} ${by-80+dy*0.52-7} ${bx+6+dx} ${by-80+dy}`}
                    fill="none" stroke="#FFD60A"
strokeWidth="2" strokeWidth={1.6-fi*0.1}/>
                ))}
              </g>
            ))}

            {/* ── STREET LAMPS ── */}
            {[38, 428, 848, 1094, 1308, 1554].map((x,i)=>(
              <g key={i}>
                <line x1={x} y1="390" x2={x} y2="342" stroke="rgba(255,214,10,0.65)"  strokeWidth="1.6"/>
                <path d={`M${x} 342 Q${x+14} 332 ${x+20} 336`}
                  fill="none" stroke="rgba(255,214,10,0.65)" strokeWidth="1.4"/>
                <circle cx={x+21} cy="337" r="4" fill="#FFD60A" opacity="0.9"/>
              </g>
            ))}

          </g>{/* end mask group */}
        </svg>
      </div>

      {/* ─── Footer Content (overlaid on skyline) ─── */}
      <div className="footer-main">
        <div className="footer-container">

          {/* Brand Column */}
          <div className="footer-col footer-brand-col">
            <Link to="/" className="footer-logo">
              <img src={logo} alt="CarYugo Logo" className="footer-logo-img" />
            </Link>
            <p className="footer-tagline">
              India's most trusted platform to buy and sell certified pre-owned cars.
            </p>
            <p className="footer-copy">
              © {currentYear} CarYugo — Aucwini Technologies LLP. All rights reserved.
            </p>

            <div className="footer-cities">
              <span className="footer-cities-label">Our Cities</span>
              <div className="footer-city-tags">
                {cities.map(city => (
                  <button key={city} className="footer-city-tag" onClick={() => handleCityClick(city)}>
                    {city}
                  </button>
                ))}
              </div>
            </div>
            <div className="footer-social">
              <a href="https://facebook.com" target="_blank" rel="noreferrer" className="social-icon" aria-label="Facebook">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="social-icon" aria-label="Instagram">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noreferrer" className="social-icon" aria-label="Twitter">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://youtube.com" target="_blank" rel="noreferrer" className="social-icon" aria-label="YouTube">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon fill="#002244" points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/></svg>
              </a>
            </div>
          </div>

          {/* Buy & Sell */}
          <div className="footer-col">
            <h4 className="footer-col-title">Buy &amp; Sell</h4>
            <ul className="footer-links">
              <li><Link to="/sell-car">Sell Your Car</Link></li>
              <li><Link to="/buy-cars">Browse All Cars</Link></li>
              <li><Link to="/shortlisted">Shortlisted Cars</Link></li>
              <li><Link to="/my-sells">My Listings</Link></li>
              <li><Link to="/finance">Car Finance</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div className="footer-col">
            <h4 className="footer-col-title">Services</h4>
            <ul className="footer-links">
              <li><Link to="/services/indore">Car Servicing</Link></li>
              <li><Link to="/service-cost-calculator">Service Cost Calculator</Link></li>
              <li><Link to="/locations">Car Hub Locations</Link></li>
              <li><Link to="/how-it-works">How It Works</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div className="footer-col">
            <h4 className="footer-col-title">Company</h4>
            <ul className="footer-links">
              <li><Link to="/about">About Us</Link></li>
            </ul>
            <h4 className="footer-col-title footer-col-title--spaced">Contact</h4>
            <div className="footer-contact-info">
              <div className="footer-contact-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.5 2 2 0 0 1 3.6 2.32h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.13 6.13l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span>+91 94253 37223</span>
              </div>
              <div className="footer-contact-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>support@caryugo.com</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </footer>
  );
};

export default Footer;